/**
 * Stable operator-QC taxonomy for Echo feedback.
 *
 * Category IDs are persistence/API identities. Labels and icon keys are
 * snapshotted onto each report so a future presentation change does not alter
 * historical feedback. Icon keys are intentionally a closed allowlist; the UI
 * maps them to statically imported Lucide components.
 */

export const FEEDBACK_TAXONOMY_VERSION = "2026-07-12.v1" as const;

export const FEEDBACK_ICON_KEYS = [
  "crop",
  "wand-sparkles",
  "smile-plus",
  "scan-line",
  "copy",
  "images",
  "timer-reset",
  "audio-lines",
  "ellipsis",
] as const;

export type FeedbackIconKey = (typeof FEEDBACK_ICON_KEYS)[number];

export const FEEDBACK_CATEGORY_IDS = [
  "framing_crop",
  "i2v_animation",
  "subject_expression",
  "visual_artifact",
  "repetition",
  "media_selection",
  "timing_pacing",
  "audio_music",
  "other",
] as const;

export type FeedbackCategoryId = (typeof FEEDBACK_CATEGORY_IDS)[number];

export type FeedbackCategoryDefinition = Readonly<{
  id: FeedbackCategoryId;
  label: string;
  iconKey: FeedbackIconKey;
}>;

export type FeedbackCategorySnapshot = {
  id: FeedbackCategoryId;
  label: string;
  iconKey: FeedbackIconKey;
};

export const FEEDBACK_CATEGORIES = [
  {
    id: "framing_crop",
    label: "Framing & crop",
    iconKey: "crop",
  },
  {
    id: "i2v_animation",
    label: "I2V / animation",
    iconKey: "wand-sparkles",
  },
  {
    id: "subject_expression",
    label: "Subject & expression",
    iconKey: "smile-plus",
  },
  {
    id: "visual_artifact",
    label: "Visual artifact",
    iconKey: "scan-line",
  },
  {
    id: "repetition",
    label: "Repetition / duplicate",
    iconKey: "copy",
  },
  {
    id: "media_selection",
    label: "Media selection",
    iconKey: "images",
  },
  {
    id: "timing_pacing",
    label: "Timing / pacing / transition",
    iconKey: "timer-reset",
  },
  {
    id: "audio_music",
    label: "Audio / music",
    iconKey: "audio-lines",
  },
  {
    id: "other",
    label: "Other",
    iconKey: "ellipsis",
  },
] as const satisfies readonly FeedbackCategoryDefinition[];

const CATEGORY_BY_ID = new Map<FeedbackCategoryId, FeedbackCategoryDefinition>(
  FEEDBACK_CATEGORIES.map((category) => [category.id, category]),
);

const CATEGORY_ORDER = new Map<FeedbackCategoryId, number>(
  FEEDBACK_CATEGORY_IDS.map((id, index) => [id, index]),
);

export function isFeedbackCategoryId(
  value: unknown,
): value is FeedbackCategoryId {
  return (
    typeof value === "string" && CATEGORY_BY_ID.has(value as FeedbackCategoryId)
  );
}

/**
 * Remove duplicates and restore taxonomy order. Unknown values are rejected so
 * new reports cannot persist presentation strings or unversioned categories.
 */
export function canonicalizeFeedbackCategoryIds(
  values: readonly unknown[],
): FeedbackCategoryId[] {
  const unique = new Set<FeedbackCategoryId>();
  for (const value of values) {
    if (!isFeedbackCategoryId(value)) {
      throw new TypeError(`Unknown feedback category: ${String(value)}`);
    }
    unique.add(value);
  }
  return [...unique].sort(
    (left, right) =>
      (CATEGORY_ORDER.get(left) ?? Number.MAX_SAFE_INTEGER) -
      (CATEGORY_ORDER.get(right) ?? Number.MAX_SAFE_INTEGER),
  );
}

export function snapshotFeedbackCategories(
  values: readonly unknown[],
): FeedbackCategorySnapshot[] {
  return canonicalizeFeedbackCategoryIds(values).map((id) => {
    const category = CATEGORY_BY_ID.get(id);
    if (!category) {
      // The canonicalizer and compile-time category table make this impossible;
      // retaining the assertion keeps future refactors fail-closed.
      throw new TypeError(`Missing feedback category definition: ${id}`);
    }
    return { ...category };
  });
}

type LegacyCategoryMatcher = Readonly<{
  id: Exclude<FeedbackCategoryId, "other">;
  patterns: readonly RegExp[];
}>;

/**
 * Deterministic, intentionally conservative inference for legacy v1 comments.
 * This is migration/display compatibility only; newly submitted feedback is
 * never silently classified. Multi-label matches are returned in taxonomy
 * order, with `other` as the explicit no-match fallback.
 */
const LEGACY_MATCHERS: readonly LegacyCategoryMatcher[] = [
  {
    id: "framing_crop",
    patterns: [
      /\b(crop(?:ped|ping)?|framing|frame[sd]? out|zoom(?:ed)?|black bars?)\b/i,
      /\b(rotat(?:e|ed|ion)|orientation|portrait|landscape|head cut|body cut)\b/i,
      /\b(encuadr\w*|recort\w*|cortad[oa]|barras? negras?)\b/i,
    ],
  },
  {
    id: "i2v_animation",
    patterns: [
      /\b(i2v|image[- ]to[- ]video|animat\w*|motion|movement|moving)\b/i,
      /\b(seesaw|windy|parallax|ken burns|should move|too static)\b/i,
      /\b(animaci[oó]n|movimiento|se mueve|est[aá]tic[oa])\b/i,
    ],
  },
  {
    id: "subject_expression",
    patterns: [
      /\b(smile|smiling|frown|expression|wrong subject|face changed|identity)\b/i,
      /\b(full body|important (?:person|animal|subject)|wrong (?:person|animal))\b/i,
      /\b(sonrisa|sonr[ií]e|ce[nñ]o|expresi[oó]n|cara cambi\w*|sujeto)\b/i,
    ],
  },
  {
    id: "visual_artifact",
    patterns: [
      /\b(artifact|jitter|flicker|glitch|uncanny|warped?|deform\w*|distort\w*)\b/i,
      /\b(low[- ]?res(?:olution)?|blurr\w*|double frame|ghosting|extra (?:arm|hand|limb))\b/i,
      /\b(artefacto|parpade\w*|deform\w*|borros[oa]|doble fotograma)\b/i,
    ],
  },
  {
    id: "repetition",
    patterns: [
      /\b(repeat\w*|duplicate\w*|same (?:photo|image|clip)|twice|reus\w*)\b/i,
      /\b(shown again|used again|back[- ]to[- ]back)\b/i,
      /\b(repet\w*|duplicad[oa]|misma (?:foto|imagen)|dos veces)\b/i,
    ],
  },
  {
    id: "media_selection",
    patterns: [
      /\b(wrong|poor|bad|better|stronger) (?:photo|image|video|clip|source|media)\b/i,
      /\b(media selection|source choice|unused (?:photo|image|video)|floor[- ]pointing)\b/i,
      /\b(foto|imagen|v[ií]deo) (?:equivocad[oa]|mala|mejor|sin usar)\b/i,
    ],
  },
  {
    id: "timing_pacing",
    patterns: [
      /\b(timing|pacing|transition|cut point|duration|ending|end too|sync)\b/i,
      /\b(too (?:quick|fast|slow|short|long)|rushed|abrupt|linger\w*)\b/i,
      /\b(ritmo|transici[oó]n|demasiado (?:r[aá]pid[oa]|lent[oa])|final apresurad[oa])\b/i,
    ],
  },
  {
    id: "audio_music",
    patterns: [
      /\b(audio|music|song|sound|voice|voiceover|narration|beat|bpm)\b/i,
      /\b(m[uú]sica|canci[oó]n|sonido|voz|narraci[oó]n)\b/i,
    ],
  },
];

export function inferLegacyFeedbackCategories(
  comment: string | null | undefined,
): FeedbackCategoryId[] {
  const text = comment?.trim();
  if (!text) return ["other"];
  const inferred = LEGACY_MATCHERS.filter(({ patterns }) =>
    patterns.some((pattern) => pattern.test(text)),
  ).map(({ id }) => id);
  return inferred.length > 0
    ? canonicalizeFeedbackCategoryIds(inferred)
    : ["other"];
}
